// Scales for the weather gauges: EPA AQI and WHO UV bands, Beaufort words and
// barometric tendency.

import { AQI_CATEGORIES } from "../../../shared/air";
import { uvCategory } from "../../../shared/sky/uv";

export interface Band {
  /** Fraction where the band ends (0–1). */
  to: number;
  color: string;
}

// AQI categories get equal arc length (the 301–500 band would otherwise swamp the rest).
const AQI_STOPS = [0, 50, 100, 150, 200, 300, 500];
export const AQI_BANDS: Band[] = [1, 2, 3, 4, 5, 6].map((c) => ({ to: c / 6, color: AQI_CATEGORIES[c]!.color }));
export function aqiFraction(aqi: number): number {
  for (let i = 1; i < AQI_STOPS.length; i++) {
    if (aqi <= AQI_STOPS[i]!) return (i - 1 + (aqi - AQI_STOPS[i - 1]!) / (AQI_STOPS[i]! - AQI_STOPS[i - 1]!)) / 6;
  }
  return 1;
}
// UV 0–12 with the WHO bands (0–2, 3–5, 6–7, 8–10, 11+).
export const UV_BANDS: Band[] = [
  { to: 2.5 / 12, color: uvCategory(1).color },
  { to: 5.5 / 12, color: uvCategory(4).color },
  { to: 7.5 / 12, color: uvCategory(7).color },
  { to: 10.5 / 12, color: uvCategory(9).color },
  { to: 1, color: uvCategory(11).color },
];

/** Beaufort description in mph thresholds. */
export function beaufort(kph: number | null): string {
  if (kph === null) return "No reading";
  const mph = kph / 1.609344;
  if (mph < 1) return "Calm";
  if (mph < 4) return "Light air";
  if (mph < 8) return "Light breeze";
  if (mph < 13) return "Gentle breeze";
  if (mph < 19) return "Moderate breeze";
  if (mph < 25) return "Fresh breeze";
  if (mph < 32) return "Strong breeze";
  if (mph < 39) return "Near gale";
  if (mph < 47) return "Gale";
  if (mph < 55) return "Strong gale";
  return "Storm-force";
}

/** "Rising slowly" etc. from the 3-hour change (WMO barometric tendency, roughly). */
export function pressureTrendWord(change3hHpa: number | null): { word: string; dir: "up" | "down" | "flat" } | null {
  if (change3hHpa === null) return null;
  const a = Math.abs(change3hHpa);
  if (a < 1) return { word: "Steady", dir: "flat" };
  const dir = change3hHpa > 0 ? "up" : "down";
  const verb = dir === "up" ? "Rising" : "Falling";
  if (a < 3.6) return { word: `${verb} slowly`, dir };
  if (a < 6.1) return { word: verb, dir };
  return { word: `${verb} quickly`, dir };
}

